import { combineReducers } from 'redux';
import {
    takeEvery,
    takeLatest,
    call,
    select,
    put,
    race,
    take,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import PromiseCreator from 'promise-creator';
import dedent from 'dedent';
import { SubmissionError } from 'redux-form';
import get from 'lodash.get';
import { push } from 'react-router-redux';
import request from '../api-utils/request';
import arrayMove from '../utils/array-move';
import actionKey from '../utils/action-key';
import { showSnackbarAction, dismissAction as dismissSnackbarAction } from './snackbar';
import { openPromptAction, showPromptErrorAction, closePromptAction } from './prompt';
import { showAlert } from './alert';

export const REMOVE_JOURNEY_CARD = 'journey/REMOVE_JOURNEY_CARD';
export const FETCH_JOURNEY_CARDS = 'journey/FETCH_JOURNEY_CARDS';
export const ADD_JOURNEY_CARD = 'journey/ADD_JOURNEY_CARD';
export const UPDATE_JOURNEY_CARD = 'journey/UPDATE_JOURNEY_CARD';
export const SORT_CARDS = 'journey/SORT_CARDS';

export const all = (state = [], action) => {
    switch (action.type) {
        case actionKey.success(FETCH_JOURNEY_CARDS):
        case actionKey.success(SORT_CARDS):
            return action.payload.cards
                .sort((a, b) => a.order - b.order)
                .map(card => card.journeyCardID_PK);
        case actionKey.init(SORT_CARDS):
            return action.newSort;
        case actionKey.success(REMOVE_JOURNEY_CARD):
            return state.filter(id => action.id !== id);
        case actionKey.success(ADD_JOURNEY_CARD):
            return [
                ...state,
                action.payload.card.journeyCardID_PK,
            ];
        default:
            return state;
    }
};

export const byId = (state = {}, action) => {
    switch (action.type) {
        case actionKey.success(FETCH_JOURNEY_CARDS):
        case actionKey.success(SORT_CARDS):
            return {
                ...state,
                ...action.payload.cards
                    .reduce((acc, card) => {
                        acc[card.journeyCardID_PK] = card;
                        return acc;
                    }, {}),
            };
        case actionKey.success(UPDATE_JOURNEY_CARD):
            return {
                ...state,
                [action.payload.card.journeyCardID_PK]: {
                    ...(state[action.payload.card.journeyCardID_PK] || {}),
                    ...action.payload.card,
                },
            };
        case actionKey.success(ADD_JOURNEY_CARD):
            return {
                ...state,
                [action.payload.card.journeyCardID_PK]: action.payload.card,
            };
        default:
            return state;
    }
};

export const loading = (state = null, action) => {
    switch (action.type) {
        case FETCH_JOURNEY_CARDS:
            return true;
        case actionKey.success(FETCH_JOURNEY_CARDS):
        case actionKey.failure(FETCH_JOURNEY_CARDS):
            return false;
        default:
            return state;
    }
};

export const errorReducer = (state = null, action) => {
    switch (action.type) {
        case FETCH_JOURNEY_CARDS:
        case actionKey.success(FETCH_JOURNEY_CARDS):
            return null;
        case actionKey.failure(FETCH_JOURNEY_CARDS):
            return action.error;
        default:
            return state;
    }
};


export default combineReducers({
    error: errorReducer,
    loading,
    byId,
    all,
});

/**
 * REDUX ACTIONS
 */

export const fetchJourneyCardsAction = () => ({
    type: FETCH_JOURNEY_CARDS,
});

export const updateJourneyCardAction = (payload, { resolve, reject } = {}) => ({
    type: UPDATE_JOURNEY_CARD,
    payload,
    resolve,
    reject,
});

export const addJourneyCardAction = (payload, { resolve, reject } = {}) => ({
    type: ADD_JOURNEY_CARD,
    payload,
    resolve,
    reject,
});

export const sortJourneyCardsAction = ({ newIndex, oldIndex }) => ({
    type: SORT_CARDS,
    payload: {
        newIndex,
        oldIndex,
    },
});

export const removeJourneyCardAction = id => ({
    type: REMOVE_JOURNEY_CARD,
    payload: id,
});


/**
 * SAGA LISTENERS
 */

export function* rootSaga() {
    yield [
        takeLatest(FETCH_JOURNEY_CARDS, fetchJourneyCards),
        takeLatest(ADD_JOURNEY_CARD, addJourneyCard),
        takeLatest(UPDATE_JOURNEY_CARD, updateJourneyCard),
        takeLatest(SORT_CARDS, sortCards),
        takeEvery(REMOVE_JOURNEY_CARD, removeJourneyCard),
        takeEvery(actionKey.init(SORT_CARDS), showSortProgress),
    ];
}

export function* fetchJourneyCards({ type }) {
    yield call(request, {
        url: '/api/journey/cards',
        type,
    });
}

const noop = () => null;

export function* sortCards({ type, payload }) {
    const { journeyCards } = yield select();
    const {
        oldIndex,
        newIndex,
    } = payload;
    if (oldIndex === newIndex) {
        return;
    }
    const newSort = arrayMove(journeyCards.all, oldIndex, newIndex);

    yield call(request, {
        url: '/api/journey/cards/reorder',
        type,
        options: {
            method: 'PUT',
            body: newSort,
        },
        newSort,
    });
}

export function* showSortProgress() {
    const {
        showMessage,
        cancel,
    } = yield race({
        cancel: take([
            actionKey.success(SORT_CARDS),
            actionKey.failure(SORT_CARDS),
        ]),
        showMessage: delay(1000),
    });

    if (showMessage) {
        const {
            failure,
        } = yield race({
            success: take(actionKey.success(SORT_CARDS)),
            failure: take(actionKey.failure(SORT_CARDS)),
        });
        yield put(showSnackbarAction({
            message: 'Sorting, please wait...',
            type: 'info',
            timeout: 99999,
        }));
        if (failure) {
            yield put(showSnackbarAction({
                message: 'Sorting failed, please refresh the page and try again.',
                type: 'error',
                timeout: 3000,
            }));
        } else {
            yield put(dismissSnackbarAction());
        }
    } else if (cancel.type === actionKey.failure(SORT_CARDS)) {
        yield put(showSnackbarAction({
            message: 'Sorting failed, please refresh the page and try again.',
            type: 'error',
            timeout: 3000,
        }));
    }
}


export function* removeJourneyCard({ type, payload }) {
    const { promise, resolve } = PromiseCreator();
    const { journeyCards } = yield select();
    const card = journeyCards.byId[payload];
    yield put(openPromptAction({
        resolve,
        title: 'Remove Journey Card',
        message: dedent`
            Are you sure you want to remove the "${get(card, 'heading', `#${payload}`)}" journey card?
        `,
        okButton: 'Remove',
        primary: 'ok',
        showLoading: true,
    }));
    const remove = yield promise;
    if (remove) {
        const result = yield call(request, {
            url: `/api/journey/card/${payload}`,
            options: {
                method: 'DELETE',
            },
            type,
            id: payload,
        });
        if (result.error) {
            yield put(showPromptErrorAction(result.error));
            yield removeJourneyCard({ type, payload });
            return;
        }
    }
    yield put(closePromptAction());
}

export function* updateJourneyCard({ type, payload, resolve = noop, reject = noop }) {
    const result = yield call(request, {
        url: `/api/journey/card/${payload.journeyCardID_PK}`,
        options: {
            method: 'PUT',
            body: payload,
        },
        type,
    });
    if (result.error) {
        reject(new SubmissionError({
            _error: result.error,
        }));
        return;
    }
    resolve();
    yield put(push('/manage-journey'));
    yield put(showAlert({
        title: 'My Journey Card updated',
        message: 'Your changes have been saved successfully.',
        type: 'success',
        button: 'Done',
    }));
}

export function* addJourneyCard({ type, payload, resolve = noop, reject = noop }) {
    const result = yield call(request, {
        url: '/api/journey/card',
        options: {
            method: 'POST',
            body: payload,
        },
        type,
    });
    if (result.error) {
        reject(new SubmissionError({
            _error: result.error,
        }));
        return;
    }
    yield put(push('/manage-journey'));
    yield put(showAlert({
        title: 'New My Journey Card added',
        message: 'Your changes have been saved successfully.',
        type: 'success',
        button: 'Done',
    }));
    resolve();
}

export function* handleFailure({ error }) {
    yield put(showSnackbarAction({
        message: error,
        type: 'failure',
        timeout: 5000,
    }));
}



// WEBPACK FOOTER //
// ./src/client/redux/journey-cards.js